import { distanceFormatter } from '@lifedot/utils'
import { useShortCodeDataCemeteries } from '@/hooks/use-short-code-data/use-short-code-data'
import { router } from '@lifedot/router'
import { Rating } from '@lifedot/atoms/Rating'
import Link from 'next/link'
import { FC } from 'react'
import Image from 'next/image'
import { replacementImage } from '@lifedot/constants/images'
import { Button } from '@lifedot/atoms/Button'
import { css } from '@emotion/react'
import { mq } from '@lifedot/styles/mediaQuery'
import { palette } from '@lifedot/styles/palette'
import { typography } from '@lifedot/styles/typography'
import { bestDisplayablePurchasePrices } from '@lifedot/utils'
import { EventTracker } from '@lifedot/tracking'

const styles = {
  root: css({
    display: 'flex',
    justifyContent: 'space-between',
    [mq('sp')]: {
      flexDirection: 'column'
    }
  }),
  title: css({
    display: 'block',
    fontWeight: 'bold'
  }),
  review: css({
    display: 'flex',
    alignItems: 'center'
  }),
  info: css({
    marginTop: 8,
    dt: {
      width: 70,
      color: palette.gray6,
      display: 'inline-block',
      position: 'relative',
      marginRight: 4,
      [mq('sp')]: {
        width: 62
      },
      ':after': {
        content: '"："',
        position: 'absolute',
        right: 0
      }
    },
    dd: {
      display: 'inline-block'
    }
  }),
  imageWrap: css({
    width: '33%',
    flexShrink: 0,
    [mq('sp')]: {
      width: '100%',
      marginTop: 12
    }
  }),
  img: css({
    display: 'flex',
    fontSize: 0,
    [mq('sp')]: {
      height: 120,
      overflow: 'hidden'
    }
  }),
  price: css({
    color: palette.alert,
    fontWeight: 'bold'
  }),
  button: css({
    display: 'block',
    textDecoration: 'none',
    marginTop: 12
  }),
  space: css({
    padding: '0 8px'
  }),
  textLink: css({
    color: palette.main03
  })
}

interface CemeteryInfoProps {
  cemeteryId: number
}

export const CemeteryInfo: FC<CemeteryInfoProps> = ({ cemeteryId }) => {
  const [cemetery] = useShortCodeDataCemeteries([cemeteryId])
  if (!cemetery) return null
  const {
    name,
    review_summary: reviewSummary,
    address,
    nearest_station: station,
    cemetery_types: cemeteryTypes,
    sects,
    section_types: sectionTypes,
    main_image: mainImage,
    requestable
  } = cemetery
  const price = bestDisplayablePurchasePrices(cemetery.best_price_by_category)

  return (
    <div css={styles.root}>
      <div>
        <EventTracker label={`tabbedCemeteryCard_${cemeteryId}`} action="click">
          <Link href={router.cemetery(cemeteryId)} passHref prefetch={false}>
            <a css={[typography.textXL, styles.title]}>{name}</a>
          </Link>
        </EventTracker>
        {reviewSummary.total_score && (
          <div css={[typography.textS, styles.review]}>
            <Rating point={reviewSummary.total_score} />（
            <Link
              href={router.review(cemeteryId, reviewSummary.count)}
              passHref
              prefetch={false}
            >
              <a css={styles.textLink}>{reviewSummary.count}件</a>
            </Link>
            ）
          </div>
        )}
        <dl css={[typography.textS, styles.info]}>
          <div>
            <dt>所在地</dt>
            <dd>{address}</dd>
          </div>
          {station.distance && (
            <div>
              <dt>アクセス</dt>
              <dd>
                {station.name}駅から{distanceFormatter(station.distance)}
                <Link
                  href={router.access(cemeteryId)}
                  passHref
                  prefetch={false}
                >
                  <a css={[typography.textS, styles.textLink, styles.space]}>
                    地図を見る
                  </a>
                </Link>
              </dd>
            </div>
          )}
          <div>
            <dt>霊園種別</dt>
            <dd>
              {cemeteryTypes
                .filter(({ name }) => name !== '公営以外')
                .map(({ name }) => name)}
            </dd>
            <span css={styles.space}>/</span>
            <dt>宗旨宗派</dt>
            <dd>{sects.map(({ name }) => name).join('/')}</dd>
          </div>
          <div>
            <dt>区画種別</dt>
            <dd>{sectionTypes.map(({ name }) => name).join('/')}</dd>
          </div>
          {price?.priceNumber && (
            <div>
              <dt>参考価格</dt>
              <dd>
                <span css={styles.price}>{price.priceNumber}</span>
                {price.priceUnit}
                {price.priceSupplement}
              </dd>
            </div>
          )}
        </dl>
      </div>
      <div css={styles.imageWrap}>
        <EventTracker label={`tabbedCemeteryCard_${cemeteryId}`} action="click">
          <Link href={router.cemetery(cemeteryId)} passHref prefetch={false}>
            <a css={styles.img}>
              <Image
                src={mainImage?.path ?? replacementImage.path}
                alt={mainImage?.alt ?? name}
                width={860}
                height={484}
                objectFit="cover"
                priority
              />
            </a>
          </Link>
        </EventTracker>
        {requestable && (
          <EventTracker
            label={`tabbedCemeteryCard_${cemeteryId}`}
            action="click"
          >
            <Link
              href={router.requestsNew({ id: cemeteryId })}
              passHref
              prefetch={false}
            >
              <a href="dummy" css={styles.button}>
                <Button
                  mainText={<span>パンフレットを請求</span>}
                  color="accent"
                  as="div"
                />
              </a>
            </Link>
          </EventTracker>
        )}
      </div>
    </div>
  )
}
